import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import moment from "moment";
import {
    ModalContainer,
    ModalHeader,
    ModalContent,
    ScrollableContent,
    ModalSection,
    ModalSectionTitle,
} from "../../Styles/InputStyles";
import ModalProfileHeader from "../../Common/ModalProfileHeader";
import { capitalizeFirstLetter } from "../../../helpers";
import Config from "../../../Config";
import Tooltip from "../../Tooltip";

const ViewTransfers = ({
    setViewModal,
    selectedData,
    data,
}) => {
    const onClose = () => {
        setViewModal(false);
    };

    const transfers = data || [];

    // Get status label and color
    const getStatusInfo = (status) => {
        const statusMap = {
            [Config.AUTOPAY_QUEUE_STATUS?.PENDING]: { label: "Pending", color: "text-yellow-700" },
            [Config.AUTOPAY_QUEUE_STATUS?.SUCCESS]: { label: "Success", color: "text-green-700" },
            [Config.AUTOPAY_QUEUE_STATUS?.FAILED]: { label: "Failed", color: "text-red-700" },
            [Config.AUTOPAY_QUEUE_STATUS?.ERROR]: { label: "Failed", color: "text-red-700" },
            [Config.AUTOPAY_QUEUE_STATUS?.CANCELLED]: { label: "Cancelled", color: "text-gray-700" },
            [Config.AUTOPAY_QUEUE_STATUS?.IN_PROGRESS]: { label: "In Progress", color: "text-blue-700" },
            [Config.AUTOPAY_QUEUE_STATUS?.REVERSED]: { label: "Reversed", color: "text-purple-700" },
        };
        return statusMap[status] || { label: "--", color: "text-gray-700" };
    };

    // Check if status is FAILED or ERROR
    const isFailedStatus = (status) => {
        return status === Config.AUTOPAY_QUEUE_STATUS?.FAILED ||
            status === Config.AUTOPAY_QUEUE_STATUS?.ERROR;
    };

    return (
        <ModalContainer>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">
                    Transaction History
                </h1>
                <button type="button" onClick={onClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <ModalProfileHeader
                title="Autopay Details"
                name={capitalizeFirstLetter(selectedData?.beneficiaryName)}
                subtitle={`#${selectedData?.beneficiaryMobile}`}
                amountLabel={"Total Amount"}
                amount={selectedData?.totalAmount}
                onClose={onClose}
            />

            <ModalContent>
                <ScrollableContent>
                    <Wrapper>
                        <SectionHeader>
                            <ModalSectionTitle>
                                Transaction History
                            </ModalSectionTitle>
                            <TransferCount>
                                {transfers.length}{" "}
                                {transfers.length === 1
                                    ? "Transfer"
                                    : "Transfers"}
                            </TransferCount>
                        </SectionHeader>

                        <ModalSection>
                            {transfers.length > 0 ? (
                                <div className="space-y-3">
                                    {transfers.map((transfer, index) => {
                                        const amount = Number(
                                            transfer?.amount ?? 0
                                        );

                                        const transferDate =
                                            transfer?.paidDate ||
                                            transfer?.createdAt;

                                        const statusInfo = getStatusInfo(transfer?.status);
                                        const showTooltip = isFailedStatus(transfer?.status);
                                        const tooltipMessage = transfer?.description || "Transaction failed";

                                        return (
                                            <ItemCard
                                                key={transfer?.id || index}
                                            >
                                                <div className="flex items-start gap-4">
                                                    {/* Left Section - Date and Status */}
                                                    <div className="flex-1 min-w-0">
                                                        <ItemName>
                                                            {transferDate
                                                                ? moment(transferDate).format(
                                                                    "DD MMM, YYYY"
                                                                )
                                                                : "-"}
                                                        </ItemName>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {showTooltip ? (
                                                                <Tooltip label={tooltipMessage}>
                                                                    <StatusBadge className={`${statusInfo.color} cursor-help underline decoration-dotted`}>
                                                                        {statusInfo.label}
                                                                    </StatusBadge>
                                                                </Tooltip>
                                                            ) : (
                                                                <StatusBadge className={statusInfo.color}>
                                                                    {statusInfo.label}
                                                                </StatusBadge>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Right Section - Amount */}
                                                    <Amount className="ml-auto">
                                                        ₹{amount.toLocaleString("en-IN")}
                                                    </Amount>
                                                </div>
                                            </ItemCard>
                                        );
                                    })}
                                </div>
                            ) : (
                                <EmptyState>
                                    No transfer history found
                                </EmptyState>
                            )}
                        </ModalSection>
                    </Wrapper>
                </ScrollableContent>
            </ModalContent>
        </ModalContainer>
    );
};

const Wrapper = tw.div`
    grid
    gap-3
    my-0
`;

const SectionHeader = tw.div`
    w-full
    flex
    items-center
    justify-between
    mb-1
`;

const ItemCard = tw.div`
    w-full
    px-6
    py-4
    rounded-xl
    bg-white
    border
    border-gray-200
    transition-all
    duration-200
    hover:border-gray-300
    hover:shadow-sm
`;
const ItemName = tw.div`
    text-sm
    font-semibold
    text-gray-800
    truncate
`;
const TransferCount = tw.span`
    text-xs
    text-gray-400
`;

const Amount = tw.div`
    text-sm
    font-semibold
    text-gray-900
    tabular-nums
`;

const StatusBadge = tw.span`
    inline-flex
    items-center
    rounded-full
    text-xs
    font-medium
`;

const EmptyState = tw.div`
    text-center
    py-8
    text-sm
    text-gray-400
`;

export default ViewTransfers;